import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useAuth, setAccessToken, clearTokens } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { API_BASE_URL } from '@/config/env';

export default function Login() {
  const { user, login, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const errorParam = searchParams.get('error');
    const authCode = searchParams.get('auth_code');
    const legacyToken = searchParams.get('token');
    const legacyRefresh = searchParams.get('refresh');
    const legacyGoogleToken = searchParams.get('google_token');
    const hasAuthParams = Boolean(errorParam || authCode || legacyToken || legacyRefresh || legacyGoogleToken);

    if (hasAuthParams) {
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (errorParam) {
      localStorage.removeItem('has_admin_session');
      setError(
        errorParam === 'authentication_failed'
          ? 'Authentication failed. Please try again.'
          : `Authentication error: ${errorParam}`,
      );
      return;
    }

    if (!authCode) {
      if (legacyToken || legacyRefresh || legacyGoogleToken) {
        localStorage.removeItem('has_admin_session');
      }
      return;
    }

    localStorage.removeItem('has_admin_session');

    const exchangeCode = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/auth/token-exchange`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ auth_code: authCode }),
        });

        if (!response.ok) {
          clearTokens();
          setError('Sign in failed. Please try again.');
          return;
        }

        const data = await response.json();
        setAccessToken(data.data.accessToken);

        const userLoaded = await refreshUser();
        if (!userLoaded) {
          clearTokens();
          setError('Sign in failed. Please try again.');
          return;
        }

        localStorage.setItem('has_admin_session', '1');
        navigate('/', { replace: true });
      } catch {
        clearTokens();
        setError('Network error during sign in. Please try again.');
      }
    };

    void exchangeCode();
  }, [searchParams, navigate, refreshUser]);

  useEffect(() => {
    if (user) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_top,_rgba(251,115,44,0.12),_transparent_35%),linear-gradient(180deg,#0f172a_0%,#111827_100%)] px-6">
      <Card className="w-full max-w-md border-white/10 bg-white/95 shadow-2xl backdrop-blur">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-2xl font-bold text-primary-foreground">
            A
          </div>
          <CardTitle className="text-2xl font-semibold">ATS Ready Admin</CardTitle>
          <CardDescription>
            {error ? error : 'Sign in with your admin account to access the dashboard'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={login} className="w-full" size="lg">
            <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {error ? 'Try Again' : 'Sign in with Google'}
          </Button>

          <p className="mt-4 text-center text-sm text-muted-foreground">
            Only authorized admin accounts can access this dashboard
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
